import React, { memo } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ReactComponent as USFlagIcon } from 'assets/icons/USFlag.svg';
import { ReactComponent as SpanishFlagIcon } from 'assets/icons/SpanishFlag.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/ArrowDown.svg';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { setLanguageCode } from 'modules/state-mgmt/app';

import { useStylesLanguageSwitcher, useStylesWorkerAndAdminMenu } from '../styles';

export const languageOptions = [
  { code: 'en', label: 'English', Icon: USFlagIcon },
  { code: 'es', label: 'Español', Icon: SpanishFlagIcon },
];

export interface ILanguageSwitcherProps {
  styleClass?: string;
}

const LanguageSwitcher = ({ styleClass = '' }: ILanguageSwitcherProps) => {
  const classes = useStylesLanguageSwitcher();
  const workerAndAdminMenuClasses = useStylesWorkerAndAdminMenu();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { languageCode } = useAppSelector(state => {
    return { languageCode: state.app?.languageCode };
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onChangeLanguage = (code: string) => {
    dispatch(setLanguageCode(code));
  };

  const onMenuSelectLanguage = (code: string) => {
    onChangeLanguage(code);
    handleClose();
  };

  return (
    <>
      <div className={`${classes.languageSwitcher} ${styleClass}`}>
        {languageOptions.map(lang => {
          const { code, Icon, label } = lang;
          const isCurrentLanguage = code === languageCode;
          return (
            <div
              key={code}
              className={`${classes.languageOption} ${isCurrentLanguage ? classes.languageOptionActive : ''}`}
              data-testid={`language-option-${code}`}
              onClick={() => onChangeLanguage(code)}
            >
              <Icon className={classes.flagIcon} />
              {label}
              {isCurrentLanguage && <div className={classes.borderBottom} />}
            </div>
          );
        })}
      </div>

      <div onClick={handleClick} className={classes.menuContainer} data-testid="language-menu-button">
        {languageCode === 'en' ? (
          <USFlagIcon className={classes.flagIconBig} />
        ) : (
          <SpanishFlagIcon className={classes.flagIconBig} />
        )}
        <ArrowDownIcon
          className={isOpen ? workerAndAdminMenuClasses.arrowIconRotated : workerAndAdminMenuClasses.arrowIcon}
        />
      </div>
      <Menu
        id="language-switcher-menu"
        anchorEl={anchorEl}
        open={isOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { elevation: 0 } }}
        className={workerAndAdminMenuClasses.menu}
        disableAutoFocusItem={true}
        disableScrollLock={true}
        onClose={handleClose}
      >
        {languageOptions.map(language => {
          const { code, label, Icon } = language;
          const isSelected = code === languageCode;
          return (
            <MenuItem
              key={code}
              data-testid={`language-menu-item-${code}`}
              onClick={() => onMenuSelectLanguage(code)}
              disableRipple={true}
              className={isSelected ? 'selected' : ''}
            >
              <Icon className={classes.flagIcon} />
              {label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default memo(LanguageSwitcher);
